/**
 * SlotCard — dumb renderer for ANY learner-state slot.
 *
 * Spec references:
 *   • §3   — slot payload shape
 *   • §19  — authority badge + reason affordance
 *   • §21  — frontend is DUMB: no client-side eligibility, no
 *            client-side wording, no client-side prioritisation.
 *
 * What this component does:
 *   1. Renders headline + body verbatim from the backend payload.
 *   2. Renders an authority badge when the payload's authority
 *      field is progression-touching (not "operational").
 *   3. Wires the primary_action (and optional secondary) to
 *      navigation / external links, including the telemetry_token
 *      via ack-action when no other backend route catches it.
 *   4. Surfaces a calm "Why am I seeing this?" affordance.
 *   5. Optional dismiss control when `dismissible: true`.
 *
 * What this component does NOT do:
 *   - Decide which slot to show — that's the backend's job.
 *   - Rewrite any text — `reason`, `headline`, `body`, `label`
 *     all come straight from the wire.
 *   - Implement slot priority — outer ordering is enforced by
 *     <LearnerStateHome>.
 *
 * The visual treatment is **subtle by default** so the calm
 * baseline (§18) holds — silence first. The spine variant
 * (slot="where_you_are") gets even quieter treatment: no border,
 * no card chrome, just text — because it's the always-on
 * presence and should not feel like an interrupting card.
 */
import axios from "axios";
import { useNavigate } from "react-router-dom";

import WhyAffordance from "./WhyAffordance";

const API = process.env.REACT_APP_BACKEND_URL;

// Authority labels shown beside the headline. Maps the wire enum
// (§19) to plain-English chip text. Operational authority is the
// only value that renders NO badge — it's not a progression claim.
const AUTHORITY_LABELS = {
  instructor_manual:     "Your instructor",
  instructor_confirmed:  "Your instructor",
  instructor_suggested:  "Your instructor (suggested)",
  ai_assisted:           "AI-assisted",
  ai_indicative:         "AI-assisted (early signals)",
  operational:           null,
};

function AuthorityBadge({ authority, testid }) {
  const label = AUTHORITY_LABELS[authority];
  if (!label) return null;
  const aiToned = authority?.startsWith("ai_");
  return (
    <span
      data-testid={testid || "slot-authority-badge"}
      data-authority={authority}
      className={[
        "inline-block text-[10px] uppercase tracking-[0.14em] font-bold px-2 py-0.5 rounded-md",
        aiToned
          ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
          : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
      ].join(" ")}
    >
      {label}
    </span>
  );
}

export default function SlotCard({ payload, onDismissed }) {
  const navigate = useNavigate();
  if (!payload) return null;

  const {
    slot,
    headline,
    body,
    primary_action: primary,
    secondary_action: secondary,
    reason,
    authority,
    dismissible,
    dismiss_label: dismissLabel,
    signal_key,
    telemetry_token,
    related_topics: relatedTopics,
  } = payload;

  const isSpine = slot === "where_you_are";
  const dismissText = (dismissLabel || "Dismiss").trim() || "Dismiss";

  const ack = async (actionType) => {
    if (!telemetry_token) return;
    try {
      await axios.post(
        `${API}/api/learner-state/ack-action`,
        { telemetry_token, action_type: actionType },
        { withCredentials: true },
      );
    } catch {
      // §21 — telemetry must never break the user flow.
    }
  };

  const runAction = async (action, kind) => {
    if (!action) return;
    if (action.type === "navigate" && action.target) {
      await ack(`${kind}_navigate`);
      navigate(action.target);
    } else if (action.type === "external" && action.target) {
      await ack(`${kind}_external`);
      window.location.assign(action.target);
    } else if (action.type === "call") {
      await ack(`${kind}_call`);
      // Future: support `method` + `payload` here. Step 8 introduces
      // the first slot that needs this. No-op at Step 6.
    } else if (action.type === "dismiss") {
      doDismiss();
    }
  };

  const doDismiss = async () => {
    if (!dismissible) return;
    try {
      await axios.post(
        `${API}/api/learner-state/dismiss`,
        { signal_key, slot, telemetry_token },
        { withCredentials: true },
      );
      if (typeof onDismissed === "function") onDismissed(signal_key);
    } catch {
      // Stay calm on error — the next /home call will re-evaluate.
    }
  };

  // ── Spine treatment: subtle, no card chrome ─────────────────────
  if (isSpine) {
    return (
      <section
        data-testid="learner-state-slot-where-you-are"
        data-slot={slot}
        data-rule-id={payload.rule_id}
        data-authority={authority}
        className="px-1 pt-1"
      >
        <div className="flex items-start gap-2 flex-wrap">
          <h2
            data-testid="slot-headline"
            className="text-base font-semibold text-slate-900 dark:text-white leading-snug"
          >
            {headline}
          </h2>
          <AuthorityBadge authority={authority} />
        </div>
        {body && (
          <p
            data-testid="slot-body"
            className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed"
          >
            {body}
          </p>
        )}
        <WhyAffordance reason={reason} signalKey={signal_key} testid="spine-why" />
      </section>
    );
  }

  // ── Default slot treatment: calm card (Steps 8–10 fodder) ───────
  return (
    <section
      data-testid={`learner-state-slot-${slot}`}
      data-slot={slot}
      data-rule-id={payload.rule_id}
      data-authority={authority}
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
    >
      <div className="flex items-start gap-2 flex-wrap">
        <h3
          data-testid="slot-headline"
          className="text-sm font-bold text-slate-900 dark:text-white leading-snug flex-1"
        >
          {headline}
        </h3>
        <AuthorityBadge authority={authority} />
      </div>
      {body && (
        <p
          data-testid="slot-body"
          className="mt-1 text-sm text-slate-600 dark:text-slate-400 leading-relaxed"
        >
          {body}
        </p>
      )}
      {(primary || secondary) && (
        <div className="mt-3 flex items-center gap-3">
          {primary && (
            <button
              type="button"
              data-testid="slot-primary-action"
              onClick={() => runAction(primary, "primary")}
              className="text-sm font-semibold text-indigo-600 dark:text-indigo-300 hover:underline"
            >
              {primary.label}
            </button>
          )}
          {secondary && (
            <button
              type="button"
              data-testid="slot-secondary-action"
              onClick={() => runAction(secondary, "secondary")}
              className="text-sm text-slate-500 dark:text-slate-400 hover:underline"
            >
              {secondary.label}
            </button>
          )}
        </div>
      )}
      {Array.isArray(relatedTopics) && relatedTopics.length > 0 && (
        /* §23 Step 16 (May 26, 2026) — quiet related-topic chips.
           Max 2 (operator-locked). Rendered as tertiary affordance,
           never primary. Each chip deep-links to the topic detail
           inside the existing Learning Hub V1 surface. */
        <div
          data-testid={`${slot}-related-topics`}
          className="mt-3 flex flex-wrap gap-2"
        >
          {relatedTopics.slice(0, 2).map((t) => (
            <button
              key={t.slug}
              type="button"
              data-testid={`${slot}-related-topic-${t.slug}`}
              onClick={async () => {
                await ack(`related_topic_${t.slug}`);
                navigate(`/learn/hub/${t.slug}`);
              }}
              className="px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200 text-[11px] font-semibold ring-1 ring-indigo-100 dark:ring-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
            >
              {t.title}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <WhyAffordance reason={reason} signalKey={signal_key} testid={`${slot}-why`} />
        {dismissible && (
          <button
            type="button"
            data-testid="slot-dismiss"
            data-dismiss-label={dismissText}
            onClick={doDismiss}
            className="text-[11px] text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 mt-2"
          >
            {dismissText}
          </button>
        )}
      </div>
    </section>
  );
}
